import * as z from 'zod';

import {
  AdvanceId,
  DisbursementId,
  DrawId,
  FeeId,
  LedgerPostingId,
  PaymentDueId,
  PaymentId,
  PayoffId,
  PrincipalModificationId,
  WorkoutPlanId,
  WriteoffId,
} from '../../BrandedIds';
import {
  AdvanceGLAccount,
  DeferredGLAccount,
  FeeReceivableGLAccount,
  FeeRevenueGLAccount,
  StatementReceivableGLAccount,
  SuspenseGLAccount,
} from './GLAccount';

// Posting kind names follow MISMO / Fannie servicer-guide convention.
// Suffixes: `Assessment` (recognize receivable), `PaymentApplication`
// (apply cash), `Curtailment` (Fannie F-1-09 principal-only paydown),
// `Disbursement` (escrow payout), `Deferral`/`Capitalization` (workout
// reclassifications), `Chargeoff` (Fannie F-1-05 / ASC 326 loss),
// `Cancellation` (undo Assessment, no cash), `Void` (undo
// PaymentApplication, cash returned), `Reversal` (generic backout).
// Reallocations compose from existing kinds, not their own family —
// see spec §"Corrections and adjustments".

const BasePosting = z.object({
  id: LedgerPostingId,
  amount: z.number().positive(),
});

// Reversal kinds are derived from their forward kind: same attribution,
// DR/CR swapped, plus `voidsPostingId`. `scopeIdField` (optional)
// registers the pair for `LoanLedger.recordReversalsFor` to pick up
// automatically — leave it off when the reversal isn't routed through
// that helper (boarding, statement cancellation, etc.).
const REVERSAL_KIND_BY_ID_FIELD_REGISTRY: Record<string, Record<string, string>> = {};

export type ReversalScopeIdField =
  | 'paymentId'
  | 'feeId'
  | 'advanceId'
  | 'drawId'
  | 'disbursementId'
  | 'modificationId'
  | 'writeoffId';

const reversalOf = <
  F extends z.ZodObject<{
    kind: z.ZodLiteral<string>;
    debitAccount: z.ZodTypeAny;
    creditAccount: z.ZodTypeAny;
    amount: z.ZodTypeAny;
  }>,
  K extends string,
>(
  forward: F,
  reversalKind: K,
  scopeIdField?: ReversalScopeIdField,
) => {
  if (scopeIdField) {
    const forwardKind = forward.shape.kind.value as string;
    (REVERSAL_KIND_BY_ID_FIELD_REGISTRY[scopeIdField] ??= {})[forwardKind] = reversalKind;
  }
  const shape = forward.shape;
  return z.object({
    ...shape,
    kind: z.literal(reversalKind),
    debitAccount: shape.creditAccount,
    creditAccount: shape.debitAccount,
    voidsPostingId: LedgerPostingId,
  });
};

// Per-scope map of forward kind → reversal kind. Scoping by id-field
// matters: `feeId` appears on both `feeAssessment` (reversed by fee
// cancellation) and `feePaymentApplication` (reversed by payment
// reversal). Each forward declares its owning id-field at its
// `reversalOf` call site.
export const REVERSAL_KIND_BY_ID_FIELD: Readonly<Record<string, Readonly<Record<string, string>>>> =
  REVERSAL_KIND_BY_ID_FIELD_REGISTRY;

// ── Assessments — DR receivable / CR revenue (or balance) ───────────

export const StatementAssessmentPosting = BasePosting.extend({
  kind: z.literal('statementAssessment'),
  debitAccount: StatementReceivableGLAccount,
  // Escrow credits `escrowObligation` (placeholder), not the real
  // `escrow` — see GLAccount.ts.
  creditAccount: z.union([
    z.enum(['interest', 'delinquentInterest']),
    z.literal('principal'),
    z.literal('escrowObligation'),
  ]),
  paymentDueId: PaymentDueId,
});
export type StatementAssessmentPosting = z.infer<typeof StatementAssessmentPosting>;

export const FeeAssessmentPosting = BasePosting.extend({
  kind: z.literal('feeAssessment'),
  debitAccount: FeeReceivableGLAccount,
  creditAccount: FeeRevenueGLAccount,
  feeId: FeeId,
  paymentDueId: PaymentDueId, // statement-associated
});
export type FeeAssessmentPosting = z.infer<typeof FeeAssessmentPosting>;

export const StandaloneFeeAssessmentPosting = BasePosting.extend({
  kind: z.literal('standaloneFeeAssessment'),
  debitAccount: FeeReceivableGLAccount,
  creditAccount: FeeRevenueGLAccount,
  feeId: FeeId,
});
export type StandaloneFeeAssessmentPosting = z.infer<typeof StandaloneFeeAssessmentPosting>;

export const CorporateAdvancePosting = BasePosting.extend({
  kind: z.literal('corporateAdvance'),
  debitAccount: AdvanceGLAccount,
  // Credits `advanceFunding` (servicer-fronted liability), not
  // `principal` — see "Servicer advances" in ledger-postings.md.
  creditAccount: z.literal('advanceFunding'),
  // Advances share a uuid with their Fee record but get a distinct
  // brand on the ledger. See `asAdvanceId`.
  advanceId: AdvanceId,
});
export type CorporateAdvancePosting = z.infer<typeof CorporateAdvancePosting>;

// ── Payment applications — DR cash / CR receivable (or balance) ─────

export const StatementPaymentApplicationPosting = BasePosting.extend({
  kind: z.literal('statementPaymentApplication'),
  debitAccount: z.literal('cash'),
  creditAccount: StatementReceivableGLAccount,
  paymentId: PaymentId,
  paymentDueId: PaymentDueId,
});
export type StatementPaymentApplicationPosting = z.infer<typeof StatementPaymentApplicationPosting>;

export const PaymentSuspensePosting = BasePosting.extend({
  kind: z.literal('paymentSuspense'),
  debitAccount: z.literal('cash'),
  creditAccount: SuspenseGLAccount,
  paymentId: PaymentId,
});
export type PaymentSuspensePosting = z.infer<typeof PaymentSuspensePosting>;

// Cash goes directly into `escrow` — borrower-designated overpayment
// on top of what the statement billed (vs. `statementPaymentApplication`
// clearing an already-assessed `escrowReceivable`).
export const PaymentEscrowContributionPosting = BasePosting.extend({
  kind: z.literal('paymentEscrowContribution'),
  debitAccount: z.literal('cash'),
  creditAccount: z.literal('escrow'),
  paymentId: PaymentId,
});
export type PaymentEscrowContributionPosting = z.infer<typeof PaymentEscrowContributionPosting>;

// Pairs with `statementPaymentApplication` clearing an
// `escrowReceivable`: converts the placeholder `escrowObligation`
// into real trust-held `escrow` now that the borrower has paid.
export const EscrowObligationSettlementPosting = BasePosting.extend({
  kind: z.literal('escrowObligationSettlement'),
  debitAccount: z.literal('escrowObligation'),
  creditAccount: z.literal('escrow'),
  paymentId: PaymentId,
  paymentDueId: PaymentDueId,
});
export type EscrowObligationSettlementPosting = z.infer<typeof EscrowObligationSettlementPosting>;

// DR `cash` for borrower-funded; one of the suspense balances for
// reallocation-style fee payments.
export const FeePaymentApplicationPosting = BasePosting.extend({
  kind: z.literal('feePaymentApplication'),
  debitAccount: z.union([z.literal('cash'), SuspenseGLAccount]),
  creditAccount: FeeReceivableGLAccount,
  paymentId: PaymentId,
  feeId: FeeId,
  paymentDueId: PaymentDueId,
});
export type FeePaymentApplicationPosting = z.infer<typeof FeePaymentApplicationPosting>;

export const StandaloneFeePaymentApplicationPosting = BasePosting.extend({
  kind: z.literal('standaloneFeePaymentApplication'),
  debitAccount: z.union([z.literal('cash'), SuspenseGLAccount]),
  creditAccount: FeeReceivableGLAccount,
  paymentId: PaymentId,
  feeId: FeeId,
});
export type StandaloneFeePaymentApplicationPosting = z.infer<typeof StandaloneFeePaymentApplicationPosting>;

// DR `servicingClearing` (not `cash`): advance recovery is owed to
// the servicer, not held in trust. Paired with `advanceReimbursement`
// in the same transaction so the clearing account nets to zero.
// Fannie A4-1-02 calls this a clearing account.
export const CorporateAdvancePaymentApplicationPosting = BasePosting.extend({
  kind: z.literal('corporateAdvancePaymentApplication'),
  debitAccount: z.literal('servicingClearing'),
  creditAccount: AdvanceGLAccount,
  paymentId: PaymentId,
  advanceId: AdvanceId,
});
export type CorporateAdvancePaymentApplicationPosting = z.infer<typeof CorporateAdvancePaymentApplicationPosting>;

// MISMO / Fannie F-1-09: "curtailment" is the precise term for
// unscheduled principal-only paydown.
export const CurtailmentPosting = BasePosting.extend({
  kind: z.literal('curtailment'),
  debitAccount: z.literal('cash'),
  creditAccount: z.literal('principal'),
  paymentId: PaymentId,
});
export type CurtailmentPosting = z.infer<typeof CurtailmentPosting>;

// Repayment-plan cash against a deferred balance. `paymentDueId`
// optional — present when the producer applies per-statement
// attribution, absent when deferred balances are pooled.
export const DeferredPaymentApplicationPosting = BasePosting.extend({
  kind: z.literal('deferredPaymentApplication'),
  debitAccount: z.literal('cash'),
  creditAccount: DeferredGLAccount,
  paymentId: PaymentId,
  paymentDueId: PaymentDueId.optional(),
});
export type DeferredPaymentApplicationPosting = z.infer<typeof DeferredPaymentApplicationPosting>;

export const PayoffStatementApplicationPosting = BasePosting.extend({
  kind: z.literal('payoffStatementApplication'),
  debitAccount: z.literal('cash'),
  creditAccount: StatementReceivableGLAccount,
  paymentId: PaymentId,
  payoffId: PayoffId,
  paymentDueId: PaymentDueId,
});
export type PayoffStatementApplicationPosting = z.infer<typeof PayoffStatementApplicationPosting>;

// `paymentDueId` optional — payoffs can clear standalone fees too.
export const PayoffFeeApplicationPosting = BasePosting.extend({
  kind: z.literal('payoffFeeApplication'),
  debitAccount: z.literal('cash'),
  creditAccount: FeeReceivableGLAccount,
  paymentId: PaymentId,
  payoffId: PayoffId,
  feeId: FeeId,
  paymentDueId: PaymentDueId.optional(),
});
export type PayoffFeeApplicationPosting = z.infer<typeof PayoffFeeApplicationPosting>;

export const PayoffPrincipalApplicationPosting = BasePosting.extend({
  kind: z.literal('payoffPrincipalApplication'),
  debitAccount: z.literal('cash'),
  creditAccount: z.union([z.literal('principal'), DeferredGLAccount]),
  paymentId: PaymentId,
  payoffId: PayoffId,
});
export type PayoffPrincipalApplicationPosting = z.infer<typeof PayoffPrincipalApplicationPosting>;

// Unbilled interest at payoff (legacy `outstandingInterest`, positive
// case). Recognized as revenue + cash inflow in one posting since no
// receivable exists for it.
export const PayoffInterestApplicationPosting = BasePosting.extend({
  kind: z.literal('payoffInterestApplication'),
  debitAccount: z.literal('cash'),
  creditAccount: z.literal('interest'),
  paymentId: PaymentId,
  payoffId: PayoffId,
});
export type PayoffInterestApplicationPosting = z.infer<typeof PayoffInterestApplicationPosting>;

// Over-collected interest at payoff (legacy `outstandingInterest`,
// negative case). `payoff.totalDue` already absorbs the credit; this
// posting is the matching ledger-side cash outflow.
export const PayoffInterestRebatePosting = BasePosting.extend({
  kind: z.literal('payoffInterestRebate'),
  debitAccount: z.literal('interest'),
  creditAccount: z.literal('cash'),
  paymentId: PaymentId,
  payoffId: PayoffId,
});
export type PayoffInterestRebatePosting = z.infer<typeof PayoffInterestRebatePosting>;

// Existing `suspense` balance applied to principal at payoff. When a
// loan has cash sitting in `suspense` (e.g. a prior partial payment
// that never accumulated into a full statement), the legacy payoff
// path consumes it to reduce what the borrower owes in cash. Mirror
// that on the ledger: DR `suspense` (drains the prior real-cash
// liability) / CR `principal` (clears the asset). No new cash inflow
// — the cash already arrived via the earlier payment's
// `paymentSuspense` posting. `paymentId` / `payoffId` attribute the
// movement to this payoff event so reports can join through.
export const PayoffSuspenseApplicationPosting = BasePosting.extend({
  kind: z.literal('payoffSuspenseApplication'),
  debitAccount: SuspenseGLAccount,
  creditAccount: z.literal('principal'),
  paymentId: PaymentId,
  payoffId: PayoffId,
});
export type PayoffSuspenseApplicationPosting = z.infer<typeof PayoffSuspenseApplicationPosting>;

// `shortageAction: 'waive-suspense'` parks the forgiven amount in
// `payoffShortfall` pending the writeoff that recognizes the loss
// via `payoffShortfallChargeoff`.
export const PayoffShortfallPosting = BasePosting.extend({
  kind: z.literal('payoffShortfall'),
  debitAccount: z.literal('payoffShortfall'),
  creditAccount: z.literal('principal'),
  paymentId: PaymentId,
  payoffId: PayoffId,
});
export type PayoffShortfallPosting = z.infer<typeof PayoffShortfallPosting>;

// ── Reclassifications — DR destination / CR source, no cash ─────────

// Deferral is split into one kind per source account to capture the
// required attribution at the type level: statement (paymentDueId),
// fee (feeId), principal (workoutPlanId only).

export const StatementDeferralPosting = BasePosting.extend({
  kind: z.literal('statementDeferral'),
  debitAccount: DeferredGLAccount,
  creditAccount: StatementReceivableGLAccount,
  workoutPlanId: WorkoutPlanId,
  paymentDueId: PaymentDueId,
});
export type StatementDeferralPosting = z.infer<typeof StatementDeferralPosting>;

export const FeeDeferralPosting = BasePosting.extend({
  kind: z.literal('feeDeferral'),
  debitAccount: z.literal('deferredFee'),
  creditAccount: FeeReceivableGLAccount,
  workoutPlanId: WorkoutPlanId,
  feeId: FeeId,
  paymentDueId: PaymentDueId.optional(),
});
export type FeeDeferralPosting = z.infer<typeof FeeDeferralPosting>;

export const PrincipalDeferralPosting = BasePosting.extend({
  kind: z.literal('principalDeferral'),
  debitAccount: z.literal('deferredPrincipal'),
  creditAccount: z.literal('principal'),
  workoutPlanId: WorkoutPlanId,
});
export type PrincipalDeferralPosting = z.infer<typeof PrincipalDeferralPosting>;

export const CapitalizationPosting = BasePosting.extend({
  kind: z.literal('capitalization'),
  debitAccount: z.literal('principal'),
  creditAccount: DeferredGLAccount,
  workoutPlanId: WorkoutPlanId,
  paymentDueId: PaymentDueId,
});
export type CapitalizationPosting = z.infer<typeof CapitalizationPosting>;

// `collectionType` picks the DR: `waived` → loss bucket
// `modification`; `deferred` → `deferredPrincipal`. Writeups go the
// other way (principal up against `chargeoff`).
export const PrincipalModificationPosting = BasePosting.extend({
  kind: z.literal('principalModification'),
  debitAccount: z.union([z.literal('modification'), z.literal('deferredPrincipal'), z.literal('principal')]),
  creditAccount: z.union([z.literal('principal'), z.literal('chargeoff')]),
  modificationId: PrincipalModificationId,
});
export type PrincipalModificationPosting = z.infer<typeof PrincipalModificationPosting>;

export const FlexModificationPosting = BasePosting.extend({
  kind: z.literal('flexModification'),
  debitAccount: DeferredGLAccount,
  creditAccount: z.literal('principal'),
  modificationId: PrincipalModificationId,
});
export type FlexModificationPosting = z.infer<typeof FlexModificationPosting>;

// Reserve application has two modes: billing (settle statement
// receivable) and payoff (zero remainder against principal).

export const ReserveStatementApplicationPosting = BasePosting.extend({
  kind: z.literal('reserveStatementApplication'),
  debitAccount: z.literal('reserve'),
  creditAccount: StatementReceivableGLAccount,
  paymentDueId: PaymentDueId,
});
export type ReserveStatementApplicationPosting = z.infer<typeof ReserveStatementApplicationPosting>;

export const ReservePrincipalApplicationPosting = BasePosting.extend({
  kind: z.literal('reservePrincipalApplication'),
  debitAccount: z.literal('reserve'),
  creditAccount: z.literal('principal'),
});
export type ReservePrincipalApplicationPosting = z.infer<typeof ReservePrincipalApplicationPosting>;

// Accumulated suspense rolls out into receivables once a payment
// triggers a full statement cover. `paymentId` is the triggering payment.
export const SuspenseStatementApplicationPosting = BasePosting.extend({
  kind: z.literal('suspenseStatementApplication'),
  debitAccount: SuspenseGLAccount,
  creditAccount: StatementReceivableGLAccount,
  paymentId: PaymentId,
  paymentDueId: PaymentDueId,
});
export type SuspenseStatementApplicationPosting = z.infer<typeof SuspenseStatementApplicationPosting>;

// `Writeoff` source-of-truth carries only `{ escrow, suspense,
// principal }` — per-fee / per-statement / per-advance chargeoff
// kinds need a richer schema first (tracked in spec).

export const PrincipalChargeoffPosting = BasePosting.extend({
  kind: z.literal('principalChargeoff'),
  debitAccount: z.literal('chargeoff'),
  creditAccount: z.union([z.literal('principal'), DeferredGLAccount]),
  writeoffId: WriteoffId,
});
export type PrincipalChargeoffPosting = z.infer<typeof PrincipalChargeoffPosting>;

// Drains a `payoffShortfall` parked at payoff time. Net effect across
// the pair: DR `chargeoff` / CR `principal`, split across events so
// the loan closes at payoff and the loss recognizes later.
export const PayoffShortfallChargeoffPosting = BasePosting.extend({
  kind: z.literal('payoffShortfallChargeoff'),
  debitAccount: z.literal('chargeoff'),
  creditAccount: z.literal('payoffShortfall'),
  writeoffId: WriteoffId,
  payoffId: PayoffId,
});
export type PayoffShortfallChargeoffPosting = z.infer<typeof PayoffShortfallChargeoffPosting>;

// ── Disbursements / refunds / draws ─────────────────────────────────

// Drains custodial cash to a third party; DR the source liability.
// Boarding seeds `cash` from the custodial liabilities so the
// per-loan slice stays non-negative under normal flows.
export const EscrowDisbursementPosting = BasePosting.extend({
  kind: z.literal('escrowDisbursement'),
  debitAccount: z.enum(['escrow', 'principal', 'suspense']),
  creditAccount: z.literal('cash'),
  disbursementId: DisbursementId,
});
export type EscrowDisbursementPosting = z.infer<typeof EscrowDisbursementPosting>;

// Lender-initiated promotion of an implicit escrow shortfall
// (negative `escrow`) to a formal `Advance{escrow}`. The credit
// brings `escrow` back up by the promoted amount; `advanceEscrow`
// holds the formal asset. Fired from
// `recordEscrowAdvanceForPaymentDeferral`.
export const EscrowAdvancePromotionPosting = BasePosting.extend({
  kind: z.literal('escrowAdvancePromotion'),
  debitAccount: z.literal('advanceEscrow'),
  creditAccount: z.literal('escrow'),
  advanceId: AdvanceId,
});
export type EscrowAdvancePromotionPosting = z.infer<typeof EscrowAdvancePromotionPosting>;

export const RefundPosting = BasePosting.extend({
  kind: z.literal('refund'),
  debitAccount: z.union([SuspenseGLAccount, z.literal('escrow')]),
  creditAccount: z.literal('cash'),
  disbursementId: DisbursementId, // refunds tracked as disbursements
});
export type RefundPosting = z.infer<typeof RefundPosting>;

// Mid-cycle servicing transfer outflow. The receivable stays settled
// (the borrower paid); `transferredFunds` carries the outgoing leg so
// the legacy un-applied-forwarded-payment view reconciles via
// `principal + principalReceivable + transferredFunds`.
export const ForwardedPaymentDisbursementPosting = BasePosting.extend({
  kind: z.literal('forwardedPaymentDisbursement'),
  debitAccount: z.literal('transferredFunds'),
  creditAccount: z.literal('cash'),
  disbursementId: DisbursementId,
});
export type ForwardedPaymentDisbursementPosting = z.infer<typeof ForwardedPaymentDisbursementPosting>;

// Same shape as `boardingPrincipal`. `cash` is untouched (funds go to
// the borrower, not through the loan's custodial pool).
export const DrawPosting = BasePosting.extend({
  kind: z.literal('draw'),
  debitAccount: z.literal('principal'),
  creditAccount: z.literal('noteHolder'),
  drawId: DrawId,
});
export type DrawPosting = z.infer<typeof DrawPosting>;

// TODO(ledger): Escrow billing / collection kinds are deferred
// pending escrow-accrual design. Accounts already exist in GLAccount.ts.

// Boarding has two halves: the principal half (DR `principal` /
// CR `noteHolder`, the credit obligation stays open across the loan's
// life) and the custodial half (DR `cash` / CR the source liability).
// `InitialBalances.principalPrepayment`, `mortgageInsurance`, and `fee`
// boarding are deferred pending design.

export const BoardingPrincipalPosting = BasePosting.extend({
  kind: z.literal('boardingPrincipal'),
  debitAccount: z.literal('principal'),
  creditAccount: z.literal('noteHolder'),
});
export type BoardingPrincipalPosting = z.infer<typeof BoardingPrincipalPosting>;

export const BoardingDeferredPrincipalPosting = BasePosting.extend({
  kind: z.literal('boardingDeferredPrincipal'),
  debitAccount: z.literal('deferredPrincipal'),
  creditAccount: z.literal('noteHolder'),
});
export type BoardingDeferredPrincipalPosting = z.infer<typeof BoardingDeferredPrincipalPosting>;

export const BoardingEscrowPosting = BasePosting.extend({
  kind: z.literal('boardingEscrow'),
  debitAccount: z.literal('cash'),
  creditAccount: z.literal('escrow'),
});
export type BoardingEscrowPosting = z.infer<typeof BoardingEscrowPosting>;

export const BoardingSuspensePosting = BasePosting.extend({
  kind: z.literal('boardingSuspense'),
  debitAccount: z.literal('cash'),
  creditAccount: SuspenseGLAccount,
});
export type BoardingSuspensePosting = z.infer<typeof BoardingSuspensePosting>;

export const BoardingHoldbackPosting = BasePosting.extend({
  kind: z.literal('boardingHoldback'),
  debitAccount: z.literal('cash'),
  creditAccount: z.literal('holdback'),
});
export type BoardingHoldbackPosting = z.infer<typeof BoardingHoldbackPosting>;

export const BoardingReservePosting = BasePosting.extend({
  kind: z.literal('boardingReserve'),
  debitAccount: z.literal('cash'),
  creditAccount: z.literal('reserve'),
});
export type BoardingReservePosting = z.infer<typeof BoardingReservePosting>;

// ── Post-boarding `initialBalances` corrections ────────────────────
// `handleUpdateLoan` mutates `initialBalances.*` on the snapshot
// after a loan has boarded. Each correction emits one posting that
// nudges the corresponding GL account by the delta. Bidirectional
// via DR/CR swap (no negative `amount`s — `BasePosting.amount` is
// positive). No reversal kinds: a wrong correction is fixed by a
// subsequent correction.

export const BalanceCorrectionPrincipalPosting = BasePosting.extend({
  kind: z.literal('balanceCorrectionPrincipal'),
  debitAccount: z.union([z.literal('principal'), z.literal('noteHolder')]),
  creditAccount: z.union([z.literal('principal'), z.literal('noteHolder')]),
});
export type BalanceCorrectionPrincipalPosting = z.infer<typeof BalanceCorrectionPrincipalPosting>;

export const BalanceCorrectionEscrowPosting = BasePosting.extend({
  kind: z.literal('balanceCorrectionEscrow'),
  debitAccount: z.union([z.literal('cash'), z.literal('escrow')]),
  creditAccount: z.union([z.literal('cash'), z.literal('escrow')]),
});
export type BalanceCorrectionEscrowPosting = z.infer<typeof BalanceCorrectionEscrowPosting>;

export const BalanceCorrectionSuspensePosting = BasePosting.extend({
  kind: z.literal('balanceCorrectionSuspense'),
  debitAccount: z.union([z.literal('cash'), SuspenseGLAccount]),
  creditAccount: z.union([z.literal('cash'), SuspenseGLAccount]),
});
export type BalanceCorrectionSuspensePosting = z.infer<typeof BalanceCorrectionSuspensePosting>;

export const BalanceCorrectionReservePosting = BasePosting.extend({
  kind: z.literal('balanceCorrectionReserve'),
  debitAccount: z.union([z.literal('cash'), z.literal('reserve')]),
  creditAccount: z.union([z.literal('cash'), z.literal('reserve')]),
});
export type BalanceCorrectionReservePosting = z.infer<typeof BalanceCorrectionReservePosting>;

export const BalanceCorrectionDeferredPrincipalPosting = BasePosting.extend({
  kind: z.literal('balanceCorrectionDeferredPrincipal'),
  debitAccount: z.union([z.literal('deferredPrincipal'), z.literal('noteHolder')]),
  creditAccount: z.union([z.literal('deferredPrincipal'), z.literal('noteHolder')]),
});
export type BalanceCorrectionDeferredPrincipalPosting = z.infer<typeof BalanceCorrectionDeferredPrincipalPosting>;

// Reversal naming: `*Cancellation` undoes an Assessment (no cash);
// `*PaymentVoid` / `CurtailmentVoid` undoes cash-bearing kinds;
// `*ChargeoffReversal` undoes a Chargeoff; `*Reversal` is the generic.

// `statementCancellation` is emitted by `recordStatementRegeneration`,
// not via `record*Reversal` — leave it unscoped.
export const StatementCancellationPosting = reversalOf(StatementAssessmentPosting, 'statementCancellation');
export type StatementCancellationPosting = z.infer<typeof StatementCancellationPosting>;

export const FeeCancellationPosting = reversalOf(FeeAssessmentPosting, 'feeCancellation', 'feeId');
export type FeeCancellationPosting = z.infer<typeof FeeCancellationPosting>;

export const StandaloneFeeCancellationPosting = reversalOf(
  StandaloneFeeAssessmentPosting,
  'standaloneFeeCancellation',
  'feeId',
);
export type StandaloneFeeCancellationPosting = z.infer<typeof StandaloneFeeCancellationPosting>;

export const CorporateAdvanceCancellationPosting = reversalOf(
  CorporateAdvancePosting,
  'corporateAdvanceCancellation',
  'advanceId',
);
export type CorporateAdvanceCancellationPosting = z.infer<typeof CorporateAdvanceCancellationPosting>;

// Payment-application reversals — all scope under `paymentId`.
export const StatementPaymentVoidPosting = reversalOf(
  StatementPaymentApplicationPosting,
  'statementPaymentVoid',
  'paymentId',
);
export type StatementPaymentVoidPosting = z.infer<typeof StatementPaymentVoidPosting>;

export const PaymentSuspenseVoidPosting = reversalOf(PaymentSuspensePosting, 'paymentSuspenseVoid', 'paymentId');
export type PaymentSuspenseVoidPosting = z.infer<typeof PaymentSuspenseVoidPosting>;

export const PaymentEscrowContributionVoidPosting = reversalOf(
  PaymentEscrowContributionPosting,
  'paymentEscrowContributionVoid',
  'paymentId',
);
export type PaymentEscrowContributionVoidPosting = z.infer<typeof PaymentEscrowContributionVoidPosting>;

export const FeePaymentVoidPosting = reversalOf(FeePaymentApplicationPosting, 'feePaymentVoid', 'paymentId');
export type FeePaymentVoidPosting = z.infer<typeof FeePaymentVoidPosting>;

export const StandaloneFeePaymentVoidPosting = reversalOf(
  StandaloneFeePaymentApplicationPosting,
  'standaloneFeePaymentVoid',
  'paymentId',
);
export type StandaloneFeePaymentVoidPosting = z.infer<typeof StandaloneFeePaymentVoidPosting>;

export const CorporateAdvancePaymentVoidPosting = reversalOf(
  CorporateAdvancePaymentApplicationPosting,
  'corporateAdvancePaymentVoid',
  'paymentId',
);
export type CorporateAdvancePaymentVoidPosting = z.infer<typeof CorporateAdvancePaymentVoidPosting>;

// ── Advance recovery — DR advanceFunding / CR cash ──────────────────
//
// Servicer-side leg paired with `corporateAdvancePaymentApplication`,
// netting `servicingClearing` to zero. Deliberately NOT attributed
// to `paymentId` (it's the servicer's action) — correlated via
// transactionId so payment-level invariants don't have to enumerate it.
export const AdvanceReimbursementPosting = BasePosting.extend({
  kind: z.literal('advanceReimbursement'),
  debitAccount: z.literal('advanceFunding'),
  creditAccount: z.literal('servicingClearing'),
  advanceId: AdvanceId,
});
export type AdvanceReimbursementPosting = z.infer<typeof AdvanceReimbursementPosting>;

export const CurtailmentVoidPosting = reversalOf(CurtailmentPosting, 'curtailmentVoid', 'paymentId');
export type CurtailmentVoidPosting = z.infer<typeof CurtailmentVoidPosting>;

export const DeferredPaymentVoidPosting = reversalOf(
  DeferredPaymentApplicationPosting,
  'deferredPaymentVoid',
  'paymentId',
);
export type DeferredPaymentVoidPosting = z.infer<typeof DeferredPaymentVoidPosting>;

export const PayoffStatementVoidPosting = reversalOf(
  PayoffStatementApplicationPosting,
  'payoffStatementVoid',
  'paymentId',
);
export type PayoffStatementVoidPosting = z.infer<typeof PayoffStatementVoidPosting>;

export const EscrowObligationSettlementReversalPosting = reversalOf(
  EscrowObligationSettlementPosting,
  'escrowObligationSettlementReversal',
  'paymentId',
);
export type EscrowObligationSettlementReversalPosting = z.infer<typeof EscrowObligationSettlementReversalPosting>;

export const PayoffFeeVoidPosting = reversalOf(PayoffFeeApplicationPosting, 'payoffFeeVoid', 'paymentId');
export type PayoffFeeVoidPosting = z.infer<typeof PayoffFeeVoidPosting>;

export const PayoffPrincipalVoidPosting = reversalOf(
  PayoffPrincipalApplicationPosting,
  'payoffPrincipalVoid',
  'paymentId',
);
export type PayoffPrincipalVoidPosting = z.infer<typeof PayoffPrincipalVoidPosting>;

export const PayoffInterestVoidPosting = reversalOf(
  PayoffInterestApplicationPosting,
  'payoffInterestVoid',
  'paymentId',
);
export type PayoffInterestVoidPosting = z.infer<typeof PayoffInterestVoidPosting>;

export const PayoffInterestRebateVoidPosting = reversalOf(
  PayoffInterestRebatePosting,
  'payoffInterestRebateVoid',
  'paymentId',
);
export type PayoffInterestRebateVoidPosting = z.infer<typeof PayoffInterestRebateVoidPosting>;

export const PayoffSuspenseApplicationVoidPosting = reversalOf(
  PayoffSuspenseApplicationPosting,
  'payoffSuspenseApplicationVoid',
  'paymentId',
);
export type PayoffSuspenseApplicationVoidPosting = z.infer<typeof PayoffSuspenseApplicationVoidPosting>;

export const PayoffShortfallVoidPosting = reversalOf(PayoffShortfallPosting, 'payoffShortfallVoid', 'paymentId');
export type PayoffShortfallVoidPosting = z.infer<typeof PayoffShortfallVoidPosting>;

export const PayoffShortfallChargeoffReversalPosting = reversalOf(
  PayoffShortfallChargeoffPosting,
  'payoffShortfallChargeoffReversal',
  'writeoffId',
);
export type PayoffShortfallChargeoffReversalPosting = z.infer<typeof PayoffShortfallChargeoffReversalPosting>;

// Reclassification reversals
export const StatementDeferralReversalPosting = reversalOf(StatementDeferralPosting, 'statementDeferralReversal');
export type StatementDeferralReversalPosting = z.infer<typeof StatementDeferralReversalPosting>;

export const FeeDeferralReversalPosting = reversalOf(FeeDeferralPosting, 'feeDeferralReversal');
export type FeeDeferralReversalPosting = z.infer<typeof FeeDeferralReversalPosting>;

export const PrincipalDeferralReversalPosting = reversalOf(PrincipalDeferralPosting, 'principalDeferralReversal');
export type PrincipalDeferralReversalPosting = z.infer<typeof PrincipalDeferralReversalPosting>;

export const CapitalizationReversalPosting = reversalOf(CapitalizationPosting, 'capitalizationReversal');
export type CapitalizationReversalPosting = z.infer<typeof CapitalizationReversalPosting>;

export const PrincipalModificationReversalPosting = reversalOf(
  PrincipalModificationPosting,
  'principalModificationReversal',
  'modificationId',
);
export type PrincipalModificationReversalPosting = z.infer<typeof PrincipalModificationReversalPosting>;

export const FlexModificationReversalPosting = reversalOf(FlexModificationPosting, 'flexModificationReversal');
export type FlexModificationReversalPosting = z.infer<typeof FlexModificationReversalPosting>;

export const ReserveStatementApplicationReversalPosting = reversalOf(
  ReserveStatementApplicationPosting,
  'reserveStatementApplicationReversal',
);
export type ReserveStatementApplicationReversalPosting = z.infer<typeof ReserveStatementApplicationReversalPosting>;

export const ReservePrincipalApplicationReversalPosting = reversalOf(
  ReservePrincipalApplicationPosting,
  'reservePrincipalApplicationReversal',
);
export type ReservePrincipalApplicationReversalPosting = z.infer<typeof ReservePrincipalApplicationReversalPosting>;

export const SuspenseStatementApplicationReversalPosting = reversalOf(
  SuspenseStatementApplicationPosting,
  'suspenseStatementApplicationReversal',
  'paymentId',
);
export type SuspenseStatementApplicationReversalPosting = z.infer<typeof SuspenseStatementApplicationReversalPosting>;

// Loss-recognition reversals
export const PrincipalChargeoffReversalPosting = reversalOf(
  PrincipalChargeoffPosting,
  'principalChargeoffReversal',
  'writeoffId',
);
export type PrincipalChargeoffReversalPosting = z.infer<typeof PrincipalChargeoffReversalPosting>;

// Disbursement / refund / draw reversals
export const EscrowDisbursementReversalPosting = reversalOf(
  EscrowDisbursementPosting,
  'escrowDisbursementReversal',
  'disbursementId',
);
export type EscrowDisbursementReversalPosting = z.infer<typeof EscrowDisbursementReversalPosting>;

// Voided when the payment-deferral plan cancels and the
// `Advance{escrow}` flips to CANCELLED — returns the promoted
// balance to the implicit (negative `escrow`) position.
export const EscrowAdvancePromotionReversalPosting = reversalOf(
  EscrowAdvancePromotionPosting,
  'escrowAdvancePromotionReversal',
  'advanceId',
);
export type EscrowAdvancePromotionReversalPosting = z.infer<typeof EscrowAdvancePromotionReversalPosting>;

export const RefundReversalPosting = reversalOf(RefundPosting, 'refundReversal', 'disbursementId');
export type RefundReversalPosting = z.infer<typeof RefundReversalPosting>;

export const ForwardedPaymentDisbursementReversalPosting = reversalOf(
  ForwardedPaymentDisbursementPosting,
  'forwardedPaymentDisbursementReversal',
);
export type ForwardedPaymentDisbursementReversalPosting = z.infer<typeof ForwardedPaymentDisbursementReversalPosting>;

export const DrawReversalPosting = reversalOf(DrawPosting, 'drawReversal', 'drawId');
export type DrawReversalPosting = z.infer<typeof DrawReversalPosting>;

// Advance recovery reversals
export const AdvanceReimbursementReversalPosting = reversalOf(
  AdvanceReimbursementPosting,
  'advanceReimbursementReversal',
);
export type AdvanceReimbursementReversalPosting = z.infer<typeof AdvanceReimbursementReversalPosting>;

// Boarding reversals
export const BoardingPrincipalReversalPosting = reversalOf(BoardingPrincipalPosting, 'boardingPrincipalReversal');
export type BoardingPrincipalReversalPosting = z.infer<typeof BoardingPrincipalReversalPosting>;

export const BoardingDeferredPrincipalReversalPosting = reversalOf(
  BoardingDeferredPrincipalPosting,
  'boardingDeferredPrincipalReversal',
);
export type BoardingDeferredPrincipalReversalPosting = z.infer<typeof BoardingDeferredPrincipalReversalPosting>;

export const BoardingEscrowReversalPosting = reversalOf(BoardingEscrowPosting, 'boardingEscrowReversal');
export type BoardingEscrowReversalPosting = z.infer<typeof BoardingEscrowReversalPosting>;

export const BoardingSuspenseReversalPosting = reversalOf(BoardingSuspensePosting, 'boardingSuspenseReversal');
export type BoardingSuspenseReversalPosting = z.infer<typeof BoardingSuspenseReversalPosting>;

export const BoardingHoldbackReversalPosting = reversalOf(BoardingHoldbackPosting, 'boardingHoldbackReversal');
export type BoardingHoldbackReversalPosting = z.infer<typeof BoardingHoldbackReversalPosting>;

export const BoardingReserveReversalPosting = reversalOf(BoardingReservePosting, 'boardingReserveReversal');
export type BoardingReserveReversalPosting = z.infer<typeof BoardingReserveReversalPosting>;

// ── Posting union ───────────────────────────────────────────────────

export const Posting = z.discriminatedUnion('kind', [
  // Assessments
  StatementAssessmentPosting,
  FeeAssessmentPosting,
  StandaloneFeeAssessmentPosting,
  CorporateAdvancePosting,
  // Payment applications
  StatementPaymentApplicationPosting,
  PaymentSuspensePosting,
  PaymentEscrowContributionPosting,
  FeePaymentApplicationPosting,
  StandaloneFeePaymentApplicationPosting,
  CorporateAdvancePaymentApplicationPosting,
  CurtailmentPosting,
  DeferredPaymentApplicationPosting,
  PayoffStatementApplicationPosting,
  EscrowObligationSettlementPosting,
  PayoffFeeApplicationPosting,
  PayoffPrincipalApplicationPosting,
  PayoffInterestApplicationPosting,
  PayoffInterestRebatePosting,
  PayoffSuspenseApplicationPosting,
  PayoffShortfallPosting,
  // Reclassifications
  StatementDeferralPosting,
  FeeDeferralPosting,
  PrincipalDeferralPosting,
  CapitalizationPosting,
  PrincipalModificationPosting,
  FlexModificationPosting,
  ReserveStatementApplicationPosting,
  ReservePrincipalApplicationPosting,
  SuspenseStatementApplicationPosting,
  // Loss recognition
  PrincipalChargeoffPosting,
  PayoffShortfallChargeoffPosting,
  // Disbursements / refunds / draws
  EscrowDisbursementPosting,
  EscrowAdvancePromotionPosting,
  RefundPosting,
  ForwardedPaymentDisbursementPosting,
  DrawPosting,
  // Advance recovery
  AdvanceReimbursementPosting,
  // Boarding
  BoardingPrincipalPosting,
  BoardingDeferredPrincipalPosting,
  BoardingEscrowPosting,
  BoardingSuspensePosting,
  BoardingHoldbackPosting,
  BoardingReservePosting,
  // Post-boarding initial-balance corrections
  BalanceCorrectionPrincipalPosting,
  BalanceCorrectionEscrowPosting,
  BalanceCorrectionSuspensePosting,
  BalanceCorrectionReservePosting,
  BalanceCorrectionDeferredPrincipalPosting,
  // ── Reversals (derived via reversalOf) ────────────────────────────
  // Assessment reversals
  StatementCancellationPosting,
  FeeCancellationPosting,
  StandaloneFeeCancellationPosting,
  CorporateAdvanceCancellationPosting,
  // Payment-application reversals (voids)
  StatementPaymentVoidPosting,
  PaymentSuspenseVoidPosting,
  PaymentEscrowContributionVoidPosting,
  FeePaymentVoidPosting,
  StandaloneFeePaymentVoidPosting,
  CorporateAdvancePaymentVoidPosting,
  CurtailmentVoidPosting,
  DeferredPaymentVoidPosting,
  PayoffStatementVoidPosting,
  EscrowObligationSettlementReversalPosting,
  PayoffFeeVoidPosting,
  PayoffPrincipalVoidPosting,
  PayoffInterestVoidPosting,
  PayoffInterestRebateVoidPosting,
  PayoffSuspenseApplicationVoidPosting,
  PayoffShortfallVoidPosting,
  // Reclassification reversals
  StatementDeferralReversalPosting,
  FeeDeferralReversalPosting,
  PrincipalDeferralReversalPosting,
  CapitalizationReversalPosting,
  PrincipalModificationReversalPosting,
  FlexModificationReversalPosting,
  ReserveStatementApplicationReversalPosting,
  ReservePrincipalApplicationReversalPosting,
  SuspenseStatementApplicationReversalPosting,
  // Loss-recognition reversals
  PrincipalChargeoffReversalPosting,
  PayoffShortfallChargeoffReversalPosting,
  // Disbursement / refund / draw reversals
  EscrowDisbursementReversalPosting,
  EscrowAdvancePromotionReversalPosting,
  RefundReversalPosting,
  ForwardedPaymentDisbursementReversalPosting,
  DrawReversalPosting,
  // Advance recovery reversals
  AdvanceReimbursementReversalPosting,
  // Boarding reversals
  BoardingPrincipalReversalPosting,
  BoardingDeferredPrincipalReversalPosting,
  BoardingEscrowReversalPosting,
  BoardingSuspenseReversalPosting,
  BoardingHoldbackReversalPosting,
  BoardingReserveReversalPosting,
]);
export type Posting = z.infer<typeof Posting>;

// ── Cross-kind invariant unions ─────────────────────────────────────
//
// Some business operations move cash via multiple posting kinds. The
// invariant that "cash moved by a payment equals the payment amount"
// aggregates across these unions. Defined as TypeScript union types
// (not Zod schemas) — they are subsets of `Posting` used only for
// invariant typing. See spec §"Cross-kind invariants (cash aggregation)".

export type CashFromPaymentPosting =
  | StatementPaymentApplicationPosting
  | PaymentSuspensePosting
  | PaymentEscrowContributionPosting
  | FeePaymentApplicationPosting
  | StandaloneFeePaymentApplicationPosting
  | CorporateAdvancePaymentApplicationPosting
  | CurtailmentPosting
  | DeferredPaymentApplicationPosting
  | PayoffStatementApplicationPosting
  | PayoffFeeApplicationPosting
  | PayoffPrincipalApplicationPosting
  | PayoffInterestApplicationPosting;

export type CashOutFromVoidPosting =
  | StatementPaymentVoidPosting
  | PaymentSuspenseVoidPosting
  | PaymentEscrowContributionVoidPosting
  | FeePaymentVoidPosting
  | StandaloneFeePaymentVoidPosting
  | CorporateAdvancePaymentVoidPosting
  | CurtailmentVoidPosting
  | DeferredPaymentVoidPosting
  | PayoffStatementVoidPosting
  | PayoffFeeVoidPosting
  | PayoffPrincipalVoidPosting;

// Cash credits attributed to an inbound payment that aren't void
// postings — borrower-side credits applied within the same payment
// transaction (e.g., a payoff that rebates over-billed interest).
// Invariant 2's cashIn aggregation must subtract these so
// "sum of cash movement attributed to payment == payment.total"
// holds when a rebate is present.
export type CashRebateToPaymentPosting = PayoffInterestRebatePosting;

export const isCashFromPaymentPosting = (p: Posting): p is CashFromPaymentPosting => {
  switch (p.kind) {
    case 'statementPaymentApplication':
    case 'paymentSuspense':
    case 'feePaymentApplication':
    case 'standaloneFeePaymentApplication':
    case 'corporateAdvancePaymentApplication':
    case 'curtailment':
    case 'deferredPaymentApplication':
    case 'payoffStatementApplication':
    case 'payoffFeeApplication':
    case 'payoffPrincipalApplication':
    case 'payoffInterestApplication':
      return true;
    default:
      return false;
  }
};

export const isCashOutFromVoidPosting = (p: Posting): p is CashOutFromVoidPosting => {
  switch (p.kind) {
    case 'statementPaymentVoid':
    case 'paymentSuspenseVoid':
    case 'feePaymentVoid':
    case 'standaloneFeePaymentVoid':
    case 'corporateAdvancePaymentVoid':
    case 'curtailmentVoid':
    case 'deferredPaymentVoid':
    case 'payoffStatementVoid':
    case 'payoffFeeVoid':
    case 'payoffPrincipalVoid':
      return true;
    default:
      return false;
  }
};

export const isCashRebateToPaymentPosting = (p: Posting): p is CashRebateToPaymentPosting => {
  switch (p.kind) {
    case 'payoffInterestRebate':
      return true;
    default:
      return false;
  }
};
